import { useState, useEffect } from 'react'
import {
  DataSchemaItemFragment,
  PayrollConceptsForBaseSalaryTypeDocument,
} from 'generated/resources/graphql'

import { Box, Spinner } from '@factorialco/deprecated-design-system'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import { PayrollConceptSelect } from 'modules/compensations/components/PayrollConceptSelectField'
import { useCategorizedPayrollConcepts } from 'modules/compensations/components/PayrollConceptSelectField/CategorizedPayrollConceptUtil'
import { WizardData } from 'modules/employees/components/UnifiedAddEmployeeModal/types'

interface Props {
  field: DataSchemaItemFragment
  fieldName: keyof WizardData
  data: WizardData
  onChangeField: (fieldName: string, value: string) => void
}

export const BaseSalaryTypeField = ({ field, fieldName, data, onChangeField }: Props) => {
  const normalizeValue = (value: string | number | null | undefined): number | null => {
    if (value == null || value === '') return null
    return typeof value === 'number' ? value : Number(value)
  }

  const {
    company: { id: companyId },
  } = useCurrentSession()

  const { data: payrollData, loading: payrollConceptsLoading } = useQuery(
    PayrollConceptsForBaseSalaryTypeDocument,
    {
      variables: {
        companyId: companyId,
      },
    }
  )

  const payrollConceptsRaw = payrollData?.payroll.conceptsConnection.nodes || []
  const payrollConcepts = useCategorizedPayrollConcepts(payrollConceptsRaw, {
    excludeBaseSalary: false,
  }).sort((a, b) => a.label.localeCompare(b.label))

  const fieldValue = data[fieldName]
  const [selectedValue, setSelectedValue] = useState<number | null>(
    normalizeValue(typeof fieldValue === 'string' || typeof fieldValue === 'number' ? fieldValue : undefined)
  )

  useEffect(() => {
    const formValue = normalizeValue(
      typeof fieldValue === 'string' || typeof fieldValue === 'number' ? fieldValue : undefined
    )
    if (formValue !== selectedValue) {
      setSelectedValue(formValue)
    }
  }, [fieldValue, selectedValue])

  const handleChange = (value: unknown) => {
    const selected = Array.isArray(value) ? value[0] : value
    const normalizedId = normalizeValue(selected)
    setSelectedValue(normalizedId)
    onChangeField(fieldName, normalizedId !== null ? String(normalizedId) : '')
  }

  if (payrollConceptsLoading) {
    return (
      <Box flexGrow justifyContent="center" alignItems="center">
        <Spinner />
      </Box>
    )
  }

  return (
    <PayrollConceptSelect
      payrollConcepts={payrollConcepts}
      value={selectedValue !== null ? [selectedValue] : []}
      onChange={handleChange}
      label={field.label}
      mandatory={field.required}
    />
  )
}
